import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, Plus, RefreshCw } from 'lucide-react';
import { formatMoney, formatTaxPercent, type CurrencyCode } from '@tengesa/shared';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import { Badge, Button, Dialog, EmptyState, ErrorNotice, Field, FormError, PageHeader, Panel, Select, Spinner, TextInput, fieldErrors, formatDateTime, formatRelative, useToast } from '../../ui';

type Env = 'simulator' | 'test' | 'production';
interface Device {
  id: string;
  branchId: string;
  branchName: string;
  environment: Env;
  deviceId: number;
  serialNo: string;
  modelName: string;
  modelVersion: string;
  status: 'registered' | 'disabled';
  certificateValidTill: string | null;
  config: {
    taxpayerName: string | null;
    taxpayerTIN: string | null;
    vatNumber: string | null;
    operatingMode: string;
    taxpayerDayMaxHrs: number;
    qrUrl: string;
    applicableTaxes: { taxID: number; taxPercentBp: number | null; taxName: string }[];
  };
  fiscalDayNo: number;
  fiscalDayStatus: 'open' | 'closed';
  fiscalDayOpenedAt: string | null;
  receiptCounter: number;
  receiptGlobalNo: number;
  lastError: string | null;
  lastContactAt: string | null;
  queued: number;
  accepted: number;
  acceptedWithErrors: number;
  rejected: number;
  days: { fiscalDayNo: number; status: string; openedAt: string; receiptCount: number; lastError: string | null }[];
}
interface ReceiptRow {
  id: string;
  saleId: string | null;
  receiptType: string;
  invoiceNo: string;
  fiscalDayNo: number;
  receiptCounter: number;
  receiptGlobalNo: number;
  receiptDate: string;
  currency: string;
  totalCents: number;
  status: 'queued' | 'accepted' | 'accepted_with_errors' | 'rejected';
  attempts: number;
  lastError: string | null;
}

const ENV: Record<Env, { label: string; tone: 'gold' | 'warn' | 'ok' }> = {
  simulator: { label: 'Simulator — not ZIMRA', tone: 'gold' },
  test: { label: 'ZIMRA test environment', tone: 'warn' },
  production: { label: 'ZIMRA live', tone: 'ok' },
};
const DAY_STATUS: Record<string, string> = {
  open_pending: 'Open, not yet confirmed by ZIMRA',
  open: 'Open',
  close_requested: 'Closing: sending to ZIMRA',
  close_pending: 'Closing: ZIMRA is checking',
  close_failed: 'Closing failed',
};
const RECEIPT_STATUS: Record<ReceiptRow['status'], { label: string; tone: 'neutral' | 'ok' | 'warn' | 'danger' }> = {
  queued: { label: 'Waiting to send', tone: 'neutral' },
  accepted: { label: 'Accepted', tone: 'ok' },
  accepted_with_errors: { label: 'Accepted with errors', tone: 'warn' },
  rejected: { label: 'Refused', tone: 'danger' },
};

export function FiscalPage() {
  const { me } = useSession();
  const [registering, setRegistering] = useState(false);
  const q = useQuery({ queryKey: ['fiscal-devices'], queryFn: () => api.get<Device[]>('/api/fiscal/devices'), refetchInterval: 15_000 });
  const active = q.data?.filter((d) => d.status === 'registered') ?? [];
  const free = me.branches.filter((b) => !active.some((d) => d.branchId === b.id));
  return (
    <>
      <PageHeader
        title="ZIMRA fiscalisation"
        description="Each fiscalised branch has a fiscal device registered with ZIMRA. Every sale is signed as it is made and sent to ZIMRA in the background; the receipt carries a verification code and QR code."
        actions={free.length > 0 && <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setRegistering(true)}>Register a device</Button>}
      />
      {!me.business.vatRegistered && (
        <p className="mb-4 rounded-[8px] bg-warn-soft px-4 py-3 text-warn">Fiscalisation here is for VAT-registered businesses. Turn on VAT registration in Business settings first.</p>
      )}
      {q.isLoading && <Spinner />}
      {q.error && <ErrorNotice error={q.error} />}
      {q.data && active.length === 0 && (
        <Panel>
          <EmptyState title="No fiscal devices yet" action={<Button variant="primary" onClick={() => setRegistering(true)}>Register a device</Button>}>
            To go live you need a device ID and activation key from ZIMRA for each branch. Use the simulator to train staff first.
          </EmptyState>
        </Panel>
      )}
      <div className="space-y-6">
        {active.map((d) => <DeviceCard key={d.id} device={d} />)}
      </div>
      {registering && <RegisterDialog branches={free} onClose={() => setRegistering(false)} />}
    </>
  );
}

function DeviceCard({ device: d }: { device: Device }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [closing, setClosing] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [filter, setFilter] = useState<'problems' | ''>('');
  const receipts = useQuery({
    queryKey: ['fiscal-receipts', d.id, filter],
    queryFn: () => api.get<ReceiptRow[]>(`/api/fiscal/devices/${d.id}/receipts?limit=30${filter ? `&status=${filter}` : ''}`),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['fiscal-devices'] });
    void qc.invalidateQueries({ queryKey: ['fiscal-receipts', d.id] });
  };
  const run = useMutation({
    mutationFn: (action: 'sync' | 'refresh-config' | 'renew-certificate') => api.post<{ submitted?: number; stoppedBy?: string | null }>(`/api/fiscal/devices/${d.id}/${action}`),
    onSuccess: (r, action) => {
      refresh();
      if (action === 'sync') toast(r.stoppedBy ? `Sent ${r.submitted ?? 0}. ${r.stoppedBy}` : `Sent ${r.submitted ?? 0} to ZIMRA`, r.stoppedBy ? 'danger' : 'ok');
      else toast(action === 'refresh-config' ? 'Settings refreshed from ZIMRA' : 'Certificate renewed');
    },
  });
  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/api/fiscal/receipts/${id}/retry`),
    onSuccess: () => {
      refresh();
      toast('Sent again');
    },
  });
  const certDays = d.certificateValidTill ? Math.floor((Date.parse(d.certificateValidTill) - Date.now()) / 86_400_000) : null;
  const failed = d.days.find((x) => x.status === 'close_failed');
  const hoursOpen = d.fiscalDayOpenedAt ? (Date.now() - Date.parse(d.fiscalDayOpenedAt)) / 3_600_000 : 0;
  return (
    <Panel>
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-line px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold">
            {d.branchName} <span className="font-normal text-muted">· device {d.deviceId}</span>
          </h2>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted">
            <Badge tone={ENV[d.environment].tone}>{ENV[d.environment].label}</Badge>
            <span>S/N {d.serialNo}</span>
            <span>· {d.config.taxpayerName ?? '—'}{d.config.taxpayerTIN ? `, TIN ${d.config.taxpayerTIN}` : ''}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" icon={<RefreshCw className="size-3.5" />} busy={run.isPending && run.variables === 'sync'} onClick={() => run.mutate('sync')}>Send now</Button>
          <Button size="sm" variant="primary" disabled={d.fiscalDayStatus !== 'open' && !failed} onClick={() => setClosing(true)}>
            {failed ? 'Try closing again' : 'Close fiscal day'}
          </Button>
        </div>
      </div>

      {(d.lastError || failed) && (
        <div className="flex gap-3 border-b border-line bg-danger-soft px-5 py-3 text-danger">
          <AlertTriangle className="mt-0.5 size-5 shrink-0" aria-hidden />
          <div className="text-sm">
            <p className="font-medium">{failed ? failed.lastError : d.lastError}</p>
            <p>Sales carry on and are kept safely; they will be sent when this is resolved.</p>
          </div>
        </div>
      )}
      {d.config.operatingMode === 'Offline' && (
        <p className="border-b border-line bg-warn-soft px-5 py-2.5 text-sm text-warn">
          ZIMRA has set this device to Offline mode, which reports receipts in batch files. Tengesa sends receipts one by one (Online mode): ask ZIMRA to
          switch the device to Online mode.
        </p>
      )}
      {d.environment === 'simulator' && (
        <p className="border-b border-line bg-gold-soft px-5 py-2.5 text-sm text-gold-ink">
          Receipts from this device are marked "SIMULATED — NOT A FISCAL RECEIPT". Use it for training; register a ZIMRA device before trading.
        </p>
      )}

      <div className="grid gap-4 border-b border-line px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
        <Fact label="Fiscal day">
          {d.fiscalDayStatus === 'open' ? (
            <>
              Day {d.fiscalDayNo}, open
              <span className={clsx('block text-sm', hoursOpen > d.config.taxpayerDayMaxHrs - 2 ? 'text-warn' : 'text-muted')}>
                since {formatDateTime(d.fiscalDayOpenedAt)} · {d.receiptCounter} receipts
              </span>
            </>
          ) : (
            <>
              Closed
              <span className="block text-sm text-muted">The next sale opens day {d.fiscalDayNo + 1}</span>
            </>
          )}
        </Fact>
        <Fact label="Sent to ZIMRA">
          {d.accepted + d.acceptedWithErrors} accepted
          <span className="block text-sm text-muted">
            {d.queued} waiting{d.rejected ? ` · ${d.rejected} refused` : ''}{d.acceptedWithErrors ? ` · ${d.acceptedWithErrors} with errors` : ''}
          </span>
        </Fact>
        <Fact label="Last contact">
          {d.lastContactAt ? formatRelative(d.lastContactAt) : 'Not yet'}
          <span className="block text-sm text-muted">{d.config.operatingMode} mode · day up to {d.config.taxpayerDayMaxHrs} h</span>
        </Fact>
        <Fact label="Certificate">
          {d.environment === 'simulator' ? 'Not needed' : certDays == null ? '—' : `${certDays} days left`}
          {d.certificateValidTill && <span className={clsx('block text-sm', certDays != null && certDays < 30 ? 'text-warn' : 'text-muted')}>until {formatDateTime(d.certificateValidTill)}</span>}
        </Fact>
      </div>

      {d.days.filter((x) => x.status !== 'open').length > 0 && (
        <ul className="divide-y divide-line border-b border-line">
          {d.days
            .filter((x) => x.status !== 'open')
            .map((x) => (
              <li key={x.fiscalDayNo} className="flex justify-between gap-3 px-5 py-2.5 text-sm">
                <span>Day {x.fiscalDayNo} · {x.receiptCount} receipts · opened {formatDateTime(x.openedAt)}</span>
                <span className={x.status === 'close_failed' ? 'text-danger' : 'text-muted'}>{DAY_STATUS[x.status] ?? x.status}</span>
              </li>
            ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
        <p className="text-sm font-medium">Recent fiscal receipts</p>
        <Select aria-label="Show" className="max-w-56" value={filter} onChange={(e) => setFilter(e.target.value as 'problems' | '')}>
          <option value="">All</option>
          <option value="problems">Needing attention</option>
        </Select>
      </div>
      {receipts.isLoading && <Spinner />}
      {receipts.data?.length === 0 && <EmptyState title={filter ? 'Nothing needs attention' : 'No receipts yet'} />}
      {!!receipts.data?.length && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left">
            <thead>
              <tr className="border-y border-line text-sm text-muted">
                <th className="px-5 py-2 font-medium">Receipt</th>
                <th className="px-3 py-2 font-medium">Document</th>
                <th className="px-3 py-2 text-right font-medium">Total</th>
                <th className="px-5 py-2 font-medium">ZIMRA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {receipts.data.map((r) => (
                <tr key={r.id}>
                  <td className="px-5 py-2.5 text-sm num">
                    {r.receiptGlobalNo} <span className="text-muted">(day {r.fiscalDayNo}, #{r.receiptCounter})</span>
                    <span className="block text-xs text-muted">{r.receiptDate.replace('T', ' ')}</span>
                  </td>
                  <td className="px-3 py-2.5 text-sm">
                    {r.saleId ? <Link className="font-medium hover:underline" to={`/sales/${r.saleId}`}>{r.invoiceNo}</Link> : r.invoiceNo}
                    <span className="block text-xs text-muted">{r.receiptType === 'CreditNote' ? 'Credit note' : 'Fiscal invoice'}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right num">{formatMoney(r.totalCents, r.currency as CurrencyCode)}</td>
                  <td className="px-5 py-2.5">
                    <Badge tone={RECEIPT_STATUS[r.status].tone}>{RECEIPT_STATUS[r.status].label}</Badge>
                    {r.lastError && <span className="mt-0.5 block max-w-sm text-xs text-muted">{r.lastError}{r.status === 'queued' ? ` (attempt ${r.attempts})` : ''}</span>}
                    {r.status === 'rejected' && (
                      <button className="mt-1 block text-sm font-medium text-ledger-700 hover:underline" onClick={() => retry.mutate(r.id)}>Send again</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <details className="border-t border-line px-5 py-3 text-sm">
        <summary className="cursor-pointer font-medium">Device settings</summary>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-1 text-muted">Taxes ZIMRA set for this device</p>
            <ul>
              {d.config.applicableTaxes.map((t) => (
                <li key={t.taxID} className="num">
                  {t.taxID}: {t.taxName} {t.taxPercentBp == null ? '(exempt)' : `(${formatTaxPercent(t.taxPercentBp)}%)`}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-muted">Model {d.modelName} {d.modelVersion} · QR {d.config.qrUrl}</p>
          </div>
          <div className="flex flex-wrap content-start gap-2">
            <Button size="sm" busy={run.isPending && run.variables === 'refresh-config'} onClick={() => run.mutate('refresh-config')}>Refresh settings from ZIMRA</Button>
            {d.environment !== 'simulator' && (
              <Button size="sm" busy={run.isPending && run.variables === 'renew-certificate'} onClick={() => run.mutate('renew-certificate')}>Renew certificate</Button>
            )}
            <Button size="sm" variant="danger" onClick={() => setDisabling(true)}>Take out of use</Button>
          </div>
        </div>
        {run.error && <div className="mt-3"><ErrorNotice error={run.error} /></div>}
      </details>

      {closing && <CloseDayDialog device={d} retrying={!!failed} onClose={() => setClosing(false)} onDone={refresh} />}
      {disabling && <DisableDialog device={d} onClose={() => setDisabling(false)} onDone={refresh} />}
    </Panel>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-sm text-muted">{label}</p>
      <p className="font-medium">{children}</p>
    </div>
  );
}

function CloseDayDialog({ device, retrying, onClose, onDone }: { device: Device; retrying: boolean; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const m = useMutation({
    mutationFn: () => api.post<{ submitted: number; stoppedBy: string | null }>(`/api/fiscal/devices/${device.id}/close-day`),
    onSuccess: (r) => {
      onDone();
      toast(r.stoppedBy ? r.stoppedBy : `Fiscal day ${device.fiscalDayNo} closed with ZIMRA`, r.stoppedBy && !r.stoppedBy.startsWith('ZIMRA is still') ? 'danger' : 'ok');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={retrying ? 'Try closing the day again' : `Close fiscal day ${device.fiscalDayNo}?`}
      description={`${device.branchName}, device ${device.deviceId}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={m.isPending} onClick={() => m.mutate()}>{retrying ? 'Try again' : 'Close the day'}</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <p>
        The day's totals are signed and sent to ZIMRA. Do this once at the end of trading. The next sale starts a new fiscal day, so the till keeps working even while ZIMRA processes the close.
      </p>
      {device.queued > 0 && <p className="mt-2 text-sm text-muted">{device.queued} receipts still waiting to be sent go first.</p>}
    </Dialog>
  );
}

function DisableDialog({ device, onClose, onDone }: { device: Device; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api.post(`/api/fiscal/devices/${device.id}/disable`, { reason }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Take device ${device.deviceId} out of use?`}
      description={`${device.branchName} will stop issuing fiscal receipts until another device is registered.`}
      footer={
        <>
          <Button onClick={onClose}>Keep it</Button>
          <Button variant="danger" disabled={reason.trim().length < 3} busy={m.isPending} onClick={() => m.mutate()}>Take out of use</Button>
        </>
      }
    >
      <FormError error={m.error} />
      {(device.queued > 0 || device.fiscalDayStatus === 'open') && (
        <p className="mb-3 rounded-[8px] bg-warn-soft px-3 py-2 text-sm text-warn">Close the fiscal day and let every receipt reach ZIMRA first, or those receipts will stay unsent.</p>
      )}
      <Field label="Reason" htmlFor="dis-reason">
        <TextInput id="dis-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. replaced by new device from ZIMRA" />
      </Field>
    </Dialog>
  );
}

function RegisterDialog({ branches, onClose }: { branches: { id: string; name: string }[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState({ branchId: branches[0]?.id ?? '', environment: 'simulator' as Env, deviceId: '', serialNo: '', activationKey: '' });
  const set = <K extends keyof typeof v>(k: K, val: (typeof v)[K]) => setV((p) => ({ ...p, [k]: val }));
  const body = () => ({ ...v, deviceId: Number(v.deviceId), activationKey: v.activationKey || null });
  const check = useMutation({ mutationFn: () => api.post<{ taxPayerName: string; taxPayerTIN: string; vatNumber: string | null; deviceBranchName: string }>('/api/fiscal/verify-taxpayer', body()) });
  const m = useMutation({
    mutationFn: () => api.post('/api/fiscal/devices', body()),
    onSuccess: () => {
      toast('Fiscal device registered');
      void qc.invalidateQueries({ queryKey: ['fiscal-devices'] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  const sim = v.environment === 'simulator';
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title="Register a fiscal device"
      description="ZIMRA gives each fiscal device an ID and a one-time activation key when you apply for fiscalisation. The device key is created here and never leaves this server."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          {!sim && <Button busy={check.isPending} disabled={!v.deviceId || !v.serialNo || v.activationKey.length < 8} onClick={() => check.mutate()}>Check with ZIMRA</Button>}
          <Button variant="primary" busy={m.isPending} disabled={!v.branchId || !v.deviceId || !v.serialNo || (!sim && v.activationKey.length < 8)} onClick={() => m.mutate()}>
            Register
          </Button>
        </>
      }
    >
      <FormError error={m.error ?? check.error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Branch" htmlFor="fd-branch">
          <Select id="fd-branch" value={v.branchId} onChange={(e) => set('branchId', e.target.value)}>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        <Field label="Where receipts go" htmlFor="fd-env">
          <Select id="fd-env" value={v.environment} onChange={(e) => set('environment', e.target.value as Env)}>
            <option value="simulator">Simulator (training, not ZIMRA)</option>
            <option value="test">ZIMRA test environment</option>
            <option value="production">ZIMRA live</option>
          </Select>
        </Field>
        <Field label="Device ID" htmlFor="fd-id" error={f.deviceId} hint={sim ? 'Any number for the simulator' : 'From ZIMRA'}>
          <TextInput id="fd-id" inputMode="numeric" value={v.deviceId} onChange={(e) => set('deviceId', e.target.value.replace(/\D/g, ''))} />
        </Field>
        <Field label="Serial number" htmlFor="fd-sn" error={f.serialNo} hint="As registered with ZIMRA">
          <TextInput id="fd-sn" value={v.serialNo} onChange={(e) => set('serialNo', e.target.value)} />
        </Field>
        {!sim && (
          <Field label="Activation key" htmlFor="fd-key" error={f.activationKey} hint="8 characters, from ZIMRA; usable once">
            <TextInput id="fd-key" autoComplete="off" value={v.activationKey} onChange={(e) => set('activationKey', e.target.value.trim())} />
          </Field>
        )}
      </div>
      {check.data && (
        <p className="mt-4 rounded-[8px] bg-ok-soft px-4 py-3 text-sm text-ok">
          ZIMRA has this device for <strong>{check.data.taxPayerName}</strong> (TIN {check.data.taxPayerTIN}{check.data.vatNumber ? `, VAT ${check.data.vatNumber}` : ''}), branch {check.data.deviceBranchName}.
        </p>
      )}
      {v.environment === 'production' && (
        <p className="mt-4 rounded-[8px] bg-warn-soft px-4 py-3 text-sm text-warn">
          Live devices issue real fiscal invoices reported to ZIMRA. Register live only after testing on ZIMRA's test environment and receiving ZIMRA's approval.
        </p>
      )}
    </Dialog>
  );
}
